import { sample } from 'effector';
import { spread } from 'patronum';
import {
  changeActiveModalEv,
  changeHexPropertyEv,
  changePopupDataEv,
  changeProjectModalEv,
  changeRegionsLayerEv,
  InvestProGate,
  resetPopupDataEv,
  resetProjectModalEv,
  separateDotsEv,
  toggleShowHeatmapEv,
  toggleShowProjectsEv,
  toggleShowRegionsEv,
} from './events.js';
import {
  $activeModal,
  $altaRailwayDots,
  $altaRailwayStartDots,
  $azsPoints,
  $azsStartPoints,
  $chartData,
  $chartsRawData,
  $ferryTerminalsDots,
  $ferryTerminalsStartDots,
  $hexagons,
  $hexProperty,
  $investProDistricts,
  $investProDots,
  $investProRegions,
  $investProStartDistricts,
  $investProStartDots,
  $investProStartRegions,
  $izDots,
  $izStartDots,
  $kazakhstanPolygon,
  $kzAirportsDots,
  $kzAirportsStartDots,
  $kzHelicopterDots,
  $kzHelicopterStartDots,
  $minresPolygons,
  $minresStartPolygons,
  $popupData,
  $projectModal,
  $regionsDict,
  $regionsLayer,
  $roadsInRegions,
  $separatedDots,
  $sezDots,
  $sezPolygons,
  $sezStartDots,
  $sezStartPolygons,
  $showHeatmap,
  $showProjectsTree,
  $showRegionsTree,
  $startHexagons,
  $stoPoints,
  $stoStartPoints,
  $warehousePoints,
  $warehouseStartPoints,
} from './stores.js';
import {
  getChartsRawDataFx,
  getFeaturesFx,
  getHexagonsFx,
  getIzParticipantsDictFx,
  getIzParticipantsFx,
  getKazakhstanFx,
  getPoiFx,
  getRegionCodesFx,
  getRegionsFx,
  getRoadsInRegionsFx,
  getSezParticipantsDictFx,
  getSezParticipantsFx,
} from './effects.js';
import {
  calculateDistThresholdsEv,
  calculateHexThresholdsEv,
  calculateThresholdsEv,
} from '../gradientModel/index.js';
import {
  $activeProjects,
  $activeRegions,
  setDefaultActiveProjectsEv,
  setDefaultRegionsValueEv,
} from '../activeFiltersModel/index.js';
import { changeIndexMain } from '../../utils/hexagon-utils.js';
import { separateDots } from './utils.js';
import {
  formatChartData,
  formatChartEmployment,
  formatChartSalary,
  formatPopulationByAgeData,
} from '../../utils/charts_utils.js';
import {
  activity_order,
  age_order,
  year_order,
} from '../../dictionaries/charts_orders.js';

$showProjectsTree.on(toggleShowProjectsEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$showRegionsTree.on(toggleShowRegionsEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$showHeatmap.on(toggleShowHeatmapEv, (state, payload) => {
  if (typeof payload === 'boolean') return payload;
  return !state;
});

$regionsLayer.on(changeRegionsLayerEv, (state, payload) => payload);

$projectModal
  .on(changeProjectModalEv, (state, payload) => payload)
  .reset(resetProjectModalEv);

$activeModal.on(changeActiveModalEv, (state, payload) => {
  if (state === payload) return null;
  return payload;
});

$popupData
  .on(changePopupDataEv, (_, payload) => payload)
  .reset(resetPopupDataEv);

sample({
  clock: InvestProGate.open,
  target: [
    getFeaturesFx,
    getRegionsFx,
    getIzParticipantsFx,
    getSezParticipantsFx,
    getRoadsInRegionsFx,
    getRegionCodesFx,
    getIzParticipantsDictFx,
    getSezParticipantsDictFx,
    getHexagonsFx,
    getKazakhstanFx,
    getPoiFx,
    getChartsRawDataFx,
  ],
  // fn: () => {
  //   return {
  //     regions: invest_pro_admin.features.filter(
  //       (item) => item.properties.ADMLVL === 1
  //     ),
  //     districts: invest_pro_admin.features.filter(
  //       (item) => item.properties.ADMLVL === 2
  //     ),
  //   };
  // },
  // target: spread({
  //   targets: {
  //     regions: $investProRegions,
  //     districts: $investProDistricts,
  //   },
  // }),
});

sample({
  clock: getFeaturesFx.doneData,
  fn: (clock) => {
    const temp = {};
    clock.features.forEach((item) => {
      if (temp[item.properties.source]) {
        temp[item.properties.source].push(item);
      } else {
        temp[item.properties.source] = [item];
      }
    });
    const lvl1 = temp.regions_with_population
      .filter((item) => item.properties.lvl === 1)
      .map((item) => ({
        ...item,
        properties: {
          ...item.properties,
          index_main: item.properties.total_population,
          unemployment_rate: Number(
            item.properties.unemployment_rate.replace(',', '.')
          ),
          vrp: Number(item.properties.vrp.replace(',', '.')),
          income: Number(
            item.properties.income.replace(',', '.').replace(' ', '')
          ),
          inflation: Number(
            item.properties.inflation.replace(',', '.').replace(' ', '')
          ),
        },
      }));

    const lvl2 = temp.regions_with_population
      .filter((item) => item.properties.lvl === 2)
      .map((item) => ({
        ...item,
        properties: {
          ...item.properties,
          index_main: item.properties.total_population,
          unemployment_rate: Number(
            item.properties.unemployment_rate?.replace(',', '.') ?? 0
          ),
          vrp: Number(item.properties.vrp?.replace(',', '.')) ?? 0,
          income: Number(
            item.properties.income?.replace(',', '.').replace(' ', '') ?? 0
          ),
          inflation: Number(
            item.properties.inflation?.replace(',', '.').replace(' ', '') ?? 0
          ),
        },
      }));

    return {
      regions: lvl1,
      districts: lvl2,
      points: [
        ...temp.test_invest_points.map((item, index) => {
          return {
            ...item,
            properties: {
              ...item.properties,
              id: `invest_project_${index}`,
            },
          };
        }),
      ],
      altaRailway: temp.alta_railway.map((item, index) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            id: `alta_railway_${index}`,
          },
        };
      }),
      kzAirports: temp.kz_airports
        .filter(
          (item) =>
            item.properties.type !== 'heliport' &&
            !item.properties.name.includes('Heliport')
        )
        .map((item, index) => {
          return {
            ...item,
            properties: {
              ...item.properties,
              id: `kz_airports_${index}`,
            },
          };
        }),
      kzHelicopter: temp.kz_airports
        .filter(
          (item) =>
            item.properties.type === 'heliport' ||
            item.properties.name.includes('Heliport')
        )
        .map((item, index) => {
          return {
            ...item,
            properties: {
              ...item.properties,
              id: `kz_helicopter_${index}`,
            },
          };
        }),
      ferryTerminals: temp.ferry_terminals.map((item, index) => {
        return {
          ...item,
          geometry: {
            coordinates: item.geometry.coordinates,
            type: 'Point',
          },
          properties: {
            ...item.properties,
            id: `ferry_terminals_${index}`,
          },
        };
      }),
      minres: temp.minres.map((item, index) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            id: `minres_${index}`,
          },
        };
      }),
      sezDots: temp.sez_raw_zone_center.map((item, index) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            id: `sez_dot_${index}`,
          },
        };
      }),
      sezPolygons: temp.sez_raw_zone.map((item, index) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            id: `sez_polygon_${index}`,
          },
        };
      }),
      izDots: temp.iz_raw_zone_center.map((item, index) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            id: `iz_dot_${index}`,
          },
        };
      }),
    };
  },
  target: spread({
    targets: {
      regions: [
        $investProRegions,
        $investProStartRegions,
        calculateThresholdsEv,
      ],
      districts: [
        $investProDistricts,
        $investProStartDistricts,
        calculateDistThresholdsEv,
        setDefaultRegionsValueEv,
      ],
      points: [
        $investProDots,
        $investProStartDots,
        separateDotsEv,
        setDefaultActiveProjectsEv,
      ],
      altaRailway: [$altaRailwayStartDots, $altaRailwayDots],
      kzAirports: [$kzAirportsStartDots, $kzAirportsDots],
      kzHelicopter: [$kzHelicopterStartDots, $kzHelicopterDots],
      ferryTerminals: [$ferryTerminalsStartDots, $ferryTerminalsDots],
      minres: [$minresStartPolygons, $minresPolygons],
      sezDots: [$sezDots, $sezStartDots],
      sezPolygons: [$sezPolygons, $sezStartPolygons],
      izDots: [$izDots, $izStartDots],
    },
  }),
});

sample({
  source: [$investProStartDots, $activeRegions],
  clock: $activeProjects.updates,
  fn: ([startDots, activeRegions], activeProjects) => {
    let source = startDots;
    if (activeRegions.length > 0) {
      source = startDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      );
    }
    return source.filter((item) => activeProjects.includes(item.properties.id));
  },
  target: $investProDots,
});

sample({
  clock: getRegionsFx.doneData,
  target: $regionsDict,
});

sample({
  source: [
    $investProStartRegions,
    $investProStartDistricts,
    $startHexagons,
    $investProStartDots,
    $altaRailwayStartDots,
    $kzAirportsStartDots,
    $kzHelicopterStartDots,
    $ferryTerminalsStartDots,
    $minresStartPolygons,
    $izStartDots,
    $sezStartDots,
    $sezStartPolygons,
    $azsStartPoints,
    $warehouseStartPoints,
    $stoStartPoints,
  ],
  clock: $activeRegions.updates,
  fn: (
    [
      startRegions,
      startDistricts,
      startHexagons,
      startDots,
      altaRailwayStartDots,
      kzAirportsStartDots,
      kzHelicopterStartDots,
      ferryTerminalsStartDots,
      minresStartPolygons,
      izStartDots,
      sezStartDots,
      sezStartPolygons,
      azsStartPoints,
      warehouseStartPoints,
      stoStartPoints,
    ],
    activeRegions
  ) => {
    // if (activeRegions.length === 0) {
    //   return {
    //     regions: startRegions,
    //     districts: startDistricts,
    //     // hexagons: startHexagons,
    //     dots: startDots,
    //     altaRailway: altaRailwayStartDots,
    //     kzAirports: kzAirportsStartDots,
    //     kzHelicopter: kzHelicopterStartDots,
    //     ferryTerminals: ferryTerminalsStartDots,
    //     minres: minresStartPolygons,
    //     sezDots: sezStartDots,
    //     sezPolygons: sezStartPolygons,
    //     izDots: izStartDots,
    //   };
    // }

    return {
      regions: startRegions.filter((item) =>
        activeRegions.includes(item.properties.lvl_1_code)
      ),
      districts: startDistricts.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      hexagons: startHexagons.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      dots: startDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      altaRailway: altaRailwayStartDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      kzAirports: kzAirportsStartDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      kzHelicopter: kzHelicopterStartDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      ferryTerminals: ferryTerminalsStartDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      minres: minresStartPolygons.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      sezDots: sezStartDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      sezPolygons: sezStartPolygons.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      izDots: izStartDots.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      azsPoints: azsStartPoints.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      warehousePoints: warehouseStartPoints.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
      stoPoints: stoStartPoints.filter((item) =>
        activeRegions.includes(item.properties.lvl_2_code)
      ),
    };
  },
  target: spread({
    targets: {
      regions: $investProRegions,
      districts: $investProDistricts,
      hexagons: $hexagons,
      dots: $investProDots,
      altaRailway: $altaRailwayDots,
      kzAirports: $kzAirportsDots,
      kzHelicopter: $kzHelicopterDots,
      ferryTerminals: $ferryTerminalsDots,
      minres: $minresPolygons,
      sezDots: $sezDots,
      sezPolygons: $sezPolygons,
      izDots: $izDots,
      azsPoints: $azsPoints,
      warehousePoints: $warehousePoints,
      stoPoints: $stoPoints,
    },
  }),
});

sample({
  source: [
    $investProRegions,
    $investProStartRegions,
    $investProDistricts,
    $investProStartDistricts,
  ],
  clock: $regionsLayer.updates,
  fn: ([regions, startRegions, districts, startDistricts], regionsLayer) => {
    return {
      regions: changeIndexMain(regions, regionsLayer),
      startRegions: changeIndexMain(startRegions, regionsLayer),
      districts: changeIndexMain(districts, regionsLayer),
      startDistricts: changeIndexMain(startDistricts, regionsLayer),
    };
  },
  target: spread({
    targets: {
      regions: $investProRegions,
      startRegions: [$investProStartRegions, calculateThresholdsEv],
      districts: $investProDistricts,
      startDistricts: [$investProStartDistricts, calculateDistThresholdsEv],
    },
  }),
});

sample({
  clock: $activeModal.updates,
  fn: (clock) => clock === 'projects',
  target: toggleShowProjectsEv,
});

sample({
  clock: $activeModal.updates,
  fn: (clock) => clock === 'regions',
  target: toggleShowRegionsEv,
});

sample({
  clock: $activeModal.updates,
  fn: (clock) => clock === 'heatmap',
  target: toggleShowHeatmapEv,
});

sample({
  clock: separateDotsEv,
  fn: separateDots,
  target: $separatedDots,
});

sample({
  source: [$separatedDots, $investProStartDots],
  clock: $investProDots.updates,
  fn: ([separatedDots, startDots], clock) => {
    if (clock.length === startDots.length) {
      return separateDots(startDots);
    }

    const filteredSep = separateDots(clock);

    return Object.fromEntries(
      Object.keys(separatedDots).map((item) => {
        return [item, filteredSep[item] || []];
      })
    );
  },
  target: $separatedDots,
});

sample({
  clock: getHexagonsFx.doneData,
  fn: (clock) => {
    return clock.features.map((item) => {
      return {
        ...item,
        properties: {
          ...item.properties,
          index_main: item.properties.count_gas_station,
        },
      };
    });
  },
  target: [$startHexagons, $hexagons, calculateHexThresholdsEv],
});

sample({
  clock: changeHexPropertyEv,
  target: $hexProperty,
});

sample({
  source: [$startHexagons, $hexagons],
  clock: $hexProperty.updates,
  fn: ([startHexagons, hexagons], clock) => {
    return {
      startHexagons: startHexagons.map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main: item.properties[clock],
          },
        };
      }),
      hexagons: hexagons.map((item) => {
        return {
          ...item,
          properties: {
            ...item.properties,
            index_main: item.properties[clock],
          },
        };
      }),
    };
  },
  target: spread({
    targets: {
      startHexagons: [$startHexagons, calculateHexThresholdsEv],
      hexagons: $hexagons,
    },
  }),
});

sample({
  clock: getKazakhstanFx.doneData,
  fn: (clock) => {
    return [
      {
        type: 'Feature',
        geometry: clock,
        properties: {},
      },
    ];
  },
  target: $kazakhstanPolygon,
});

sample({
  clock: getPoiFx.doneData,
  fn: (clock) => {
    const azsPoints = [];
    const warehousePoints = [];
    const stoPoints = [];
    clock.forEach((item) => {
      const formatedFeature = {
        type: 'Feature',
        properties: {
          ...item,
        },
        geometry: {
          coordinates: [item.lon, item.lat],
          type: 'Point',
        },
      };

      switch (item.search_rubric) {
        case 'АЗС':
          azsPoints.push(formatedFeature);
          break;
        case 'Автосервис, автотехцентр':
        case 'Шиномонтаж':
          stoPoints.push(formatedFeature);
          break;
        case 'Складские услуги':
        case 'Таможенный склад':
          warehousePoints.push(formatedFeature);
          break;
        default:
          break;
      }
    });
    return {
      azsPoints,
      warehousePoints,
      stoPoints,
    };
  },
  target: spread({
    targets: {
      azsPoints: [$azsStartPoints, $azsPoints],
      warehousePoints: [$warehouseStartPoints, $warehousePoints],
      stoPoints: [$stoStartPoints, $stoPoints],
    },
  }),
});

sample({
  clock: getChartsRawDataFx.doneData,
  fn: (clock) => {
    return {
      ...clock,
      vrp_yearly: Object.fromEntries(
        Object.keys(clock.vrp_yearly).map((code) => {
          return [
            code,
            Object.keys(clock.vrp_yearly[code]).map((item) => ({
              ...clock.vrp_yearly[code][item],
              year: item,
            })),
          ];
        })
      ),
    };
  },
  target: $chartsRawData,
});

sample({
  source: $chartsRawData,
  clock: $projectModal,
  filter: (_, clock) =>
    clock && clock.properties.source === 'regions_with_population',
  fn: (source, clock) => {
    const { lvl_2_code, lvl_1_code } = clock.properties;

    const populationByAgeData = formatPopulationByAgeData(
      source.population_by_age[lvl_2_code || lvl_1_code]
    );
    const vrpData = formatChartData(
      source.vrp_yearly[lvl_1_code],
      year_order,
      'year',
      'vrp_mln_tg'
    );

    const laborPercentData = formatChartData(
      source.labor_by_activity[lvl_1_code],
      activity_order,
      'activity',
      'percent_of_total',
      'quarter'
    );

    const laborCountData = formatChartData(
      source.labor_by_activity[lvl_1_code],
      activity_order,
      'activity',
      'count_people',
      'quarter'
    );

    const poiData = [
      source.poi_stats[lvl_1_code].count_dosug,
      source.poi_stats[lvl_1_code].count_health_beauty,
      source.poi_stats[lvl_1_code].count_non_commercial_org,
      source.poi_stats[lvl_1_code].count_food_drink,
      source.poi_stats[lvl_1_code].count_services_for_people,
      source.poi_stats[lvl_1_code].count_retail,
      source.poi_stats[lvl_1_code].count_industry,
      source.poi_stats[lvl_1_code].count_finance,
      source.poi_stats[lvl_1_code].count_education,
      source.poi_stats[lvl_1_code].count_auto,
    ];

    const salaryByAgeData = formatChartSalary(
      source.salary_by_regions[lvl_1_code]
    );

    const employmentRateData = formatChartEmployment(
      source.employment_stats[lvl_1_code],
      'employment_rate_age'
    );
    const unemploymentRateData = formatChartEmployment(
      source.employment_stats[lvl_1_code],
      'unemployment_rate_age'
    );

    const tradeTurnoverData = source.trade_turnover[lvl_1_code];

    const roadsInRegionsData =
      source.roads_in_regions[lvl_2_code || lvl_1_code];

    return {
      populationByAgeData,
      vrpData,
      laborPercentData,
      laborCountData,
      poiData,
      salaryByAgeData,
      employmentRateData,
      unemploymentRateData,
      tradeTurnoverData,
      roadsInRegionsData,
    };
  },
  target: $chartData,
});

sample({
  clock: getRoadsInRegionsFx.doneData,
  target: $roadsInRegions,
});
